/**
 * The funnel event vocabulary (#1768): one typed catalogue of every named fact the onboarding funnel and #1697's
 * success measures are read from, and the closed sets every stored event is split by. Events are named
 * `<area>.<past_tense_fact>`, as `onboarding.first_successful_run` already is.
 */

/** Which way a user set up: chat with the COO, guided step-by-step screens, or Advanced (skip). `unknown` is real. */
export type SetupPath = 'chat' | 'step_by_step' | 'advanced' | 'unknown'

/** Whether the user is technical or not, by their own profile answer. `unknown` is real. */
export type UserKind = 'technical' | 'non_technical' | 'unknown'

/** Why a user left the AI account step: the PRD's four reasons, and `other` for anything else. */
export type AiAccountAbandonReason = 'no_account' | 'thought_model_included' | 'key_failed' | 'billing_error' | 'other'

/** The nine success measures of #1697, by the id each event names the measure it serves with. */
export type FunnelMeasure =
    | 'time_to_first_approved_result'
    | 'first_session_success'
    | 'activation_gap'
    | 'ai_account_connection'
    | 'profile_completion_and_naming'
    | 'team_clone_rate'
    | 'form_completion'
    | 'needs_you_response_time'
    | 'gate_abandonment'

/** What one payload field of an event may hold: a member of a closed set, or a number. Never free text. */
export type FunnelPayloadField = { kind: 'enum'; values: readonly string[] } | { kind: 'number' }

/** Who emits an entry: a live call site today, or the #1697 workstream (1 to 6) that will wire it. */
export type FunnelEmitter = { status: 'live' } | { status: 'declared'; workstream: number }

export type FunnelEventEntry = {
    name: string
    measures: readonly FunnelMeasure[]
    payload: Readonly<Record<string, FunnelPayloadField>>
    emitter: FunnelEmitter | null
    /** The bare name of the #1697 spec row this entry implements, when it implements one. */
    spec?: string
    /**
     * Recorded at most once per user (#1769): the emit path derives the row's id from the enterprise, the user and
     * the event, so a repeated trigger loses to the row already there. An emit with no user still appends, since
     * there is no one to key it on. Absent, every emit is its own row.
     */
    once?: boolean
}

export const SETUP_PATHS: readonly SetupPath[] = ['chat', 'step_by_step', 'advanced', 'unknown']

export const USER_KINDS: readonly UserKind[] = ['technical', 'non_technical', 'unknown']

export const AI_ACCOUNT_ABANDON_REASONS: readonly AiAccountAbandonReason[] = [
    'no_account',
    'thought_model_included',
    'key_failed',
    'billing_error',
    'other',
]

export const FUNNEL_MEASURES: readonly FunnelMeasure[] = [
    'time_to_first_approved_result',
    'first_session_success',
    'activation_gap',
    'ai_account_connection',
    'profile_completion_and_naming',
    'team_clone_rate',
    'form_completion',
    'needs_you_response_time',
    'gate_abandonment',
]

/** How a user signed up: the PRD's four ways in. */
export const SIGNUP_PROVIDERS = ['google', 'microsoft', 'github', 'email'] as const

/** The creation flows whose form completion is measured, one rate each. */
export const FUNNEL_FORM_FLOWS = ['task', 'teammate', 'team', 'tool'] as const

/**
 * The fields a form field error is split by (#1774): the keys of the task, teammate, team and tool create forms, and
 * `other` for a field outside them. Only the key ever leaves the browser, never what was typed into it.
 */
export const FUNNEL_FORM_FIELDS = [
    'name',
    'slug',
    'description',
    'email',
    'persona',
    'team',
    'lead',
    'role',
    'status',
    'kind',
    'schedule',
    'budget',
    'goal',
    'metrics',
    'guardrails',
    'tools',
    'skill',
    'inputs',
    'workspace',
    'successors',
    'charter',
    'concurrency',
    'other',
] as const

/**
 * The kinds of Needs-you item a funnel event is split by (#1774), worked out from the item's shape alone: a CEO gate
 * asking the CEO questions is a `question`, any other is an `approval`, and `other` keeps a future kind counted.
 */
export const FUNNEL_NEEDS_YOU_ITEM_TYPES = ['approval', 'question', 'other'] as const

/** The plans a team clone is split by. The same ids as the enterprise tiers; a plan outside them is not recorded. */
export const FUNNEL_PLANS = ['free', 'starter', 'company', 'scale', 'enterprise'] as const

/** How a CEO decides an approval prompt. */
export const FUNNEL_DECISIONS = ['approve', 'request_changes', 'reject'] as const

/** The decisions that turn a first piece of work down: rejected outright, or sent back with feedback. */
export const FUNNEL_TURNED_DOWN = ['reject', 'request_changes'] as const

/** The prompts an upgrade can start from (#1697): the team proposal, the card after a first approval, a locked slot. */
export const FUNNEL_UPGRADE_TRIGGERS = ['team_proposal', 'post_approval_card', 'locked_slot'] as const

/** The #1697 workstreams an entry's emitter may be declared against. */
const WORKSTREAMS = { min: 1, max: 6 } as const

/** The #1697 workstream that builds each flow, and so wires its events. */
const ACCESS_AND_AI_ACCOUNT: FunnelEmitter = { status: 'declared', workstream: 1 }
const CATALOG_AND_CLONING: FunnelEmitter = { status: 'declared', workstream: 2 }
const ONBOARDING_AND_NEEDS_YOU: FunnelEmitter = { status: 'declared', workstream: 4 }

const NUMBER: FunnelPayloadField = { kind: 'number' }

function oneOf(values: readonly string[]): FunnelPayloadField {
    return { kind: 'enum', values }
}

/**
 * One recovery entry (#1769): a row of the Edge cases table of #1697's Onboarding spec, under its catalogue `name`,
 * carrying the bare `spec` name it implements so `funnelSpecGaps` can audit the catalogue back to the table.
 */
function recovery(
    spec: string,
    name: string,
    emitter: FunnelEmitter,
    measures: readonly FunnelMeasure[],
    payload: Readonly<Record<string, FunnelPayloadField>> = {},
    options: { once?: boolean } = {},
): FunnelEventEntry {
    return { name, spec, measures, payload, emitter, ...options }
}

/** A recovery whose trigger already exists, and which emits its event there today. */
const LIVE: FunnelEmitter = { status: 'live' }

/**
 * Every funnel fact, in the order a new user meets them. An entry names the measures it is read for, the typed
 * payload it may carry (closed sets and numbers only, never free text), and who emits it: a live call site today,
 * or the #1697 workstream that builds the flow it happens in. `funnelCoverageGaps` keeps every entry owned.
 */
export const FUNNEL_EVENTS: readonly FunnelEventEntry[] = [
    {
        name: 'onboarding.beta_checked',
        measures: ['first_session_success'],
        payload: { result: oneOf(['admitted', 'refused']) },
        emitter: ACCESS_AND_AI_ACCOUNT,
    },
    {
        name: 'onboarding.signup_started',
        measures: ['first_session_success'],
        payload: { provider: oneOf(SIGNUP_PROVIDERS) },
        emitter: ACCESS_AND_AI_ACCOUNT,
    },
    {
        name: 'onboarding.signup_completed',
        measures: [
            'time_to_first_approved_result',
            'first_session_success',
            'activation_gap',
            'profile_completion_and_naming',
        ],
        payload: { provider: oneOf(SIGNUP_PROVIDERS) },
        emitter: ACCESS_AND_AI_ACCOUNT,
    },
    // The AI account step (#1770): the setup read records the step shown, the connect call records the connection,
    // each once per user. Leaving the step is the one recovery row `provider.step_abandoned`, below.
    {
        name: 'onboarding.ai_account_step_shown',
        measures: ['ai_account_connection'],
        payload: {},
        emitter: { status: 'live' },
        once: true,
    },
    {
        name: 'onboarding.ai_account_connected',
        measures: ['ai_account_connection'],
        payload: { elapsedMs: NUMBER },
        emitter: { status: 'live' },
        once: true,
    },
    {
        name: 'onboarding.profile_questions_answered',
        measures: ['profile_completion_and_naming'],
        payload: { answered: NUMBER, asked: NUMBER },
        emitter: ONBOARDING_AND_NEEDS_YOU,
    },
    // Setup's path (#1742): every real switch between the chat with the COO and the step-by-step screens, so the
    // switch rate can be read, and setup skipped for the dashboard in Advanced mode. `OnboardingService` records both
    // beside their own setup log entries (`setup_path_switched`, `setup_skipped`).
    {
        name: 'setup.path_switched',
        measures: ['first_session_success'],
        payload: { to: oneOf(['chat', 'step_by_step']) },
        emitter: { status: 'live' },
    },
    {
        name: 'setup.skipped',
        measures: ['first_session_success'],
        payload: {},
        emitter: { status: 'live' },
    },
    {
        name: 'onboarding.recommendations_shown',
        measures: ['team_clone_rate'],
        payload: { count: NUMBER },
        emitter: ONBOARDING_AND_NEEDS_YOU,
    },
    {
        name: 'onboarding.team_cloned',
        measures: ['team_clone_rate'],
        payload: { plan: oneOf(FUNNEL_PLANS), teamSize: NUMBER },
        emitter: CATALOG_AND_CLONING,
    },
    {
        name: 'onboarding.teammates_named',
        measures: ['profile_completion_and_naming'],
        payload: { named: NUMBER, pickedByCoo: NUMBER },
        emitter: ONBOARDING_AND_NEEDS_YOU,
    },
    {
        name: 'onboarding.first_work_proposed',
        measures: ['time_to_first_approved_result', 'first_session_success'],
        payload: { elapsedMs: NUMBER },
        emitter: ONBOARDING_AND_NEEDS_YOU,
    },
    {
        name: 'onboarding.first_approval_prompted',
        measures: ['gate_abandonment'],
        payload: {},
        emitter: ONBOARDING_AND_NEEDS_YOU,
    },
    {
        name: 'onboarding.first_approval_decided',
        measures: ['gate_abandonment'],
        payload: { decision: oneOf(FUNNEL_DECISIONS), elapsedMs: NUMBER },
        emitter: ONBOARDING_AND_NEEDS_YOU,
    },
    {
        name: 'onboarding.first_result_approved',
        measures: ['time_to_first_approved_result', 'first_session_success', 'activation_gap'],
        payload: { elapsedMs: NUMBER },
        emitter: ONBOARDING_AND_NEEDS_YOU,
    },
    {
        // The milestone #420 already records once per enterprise; the plan decision bridge emits it.
        name: 'onboarding.first_successful_run',
        measures: ['activation_gap', 'first_session_success'],
        payload: { elapsedMs: NUMBER },
        emitter: { status: 'live' },
    },
    // Needs you (#1774): an item is a waiting gate the CEO owns. The gates service records it added (once per gate)
    // and decided by a person (once per gate, with the time from added); the portal reports it opened, once per item
    // per person. Each is split by the item's type, never by the tool, payload or summary of the gate.
    {
        name: 'needs_you.item_added',
        measures: ['needs_you_response_time'],
        payload: { itemType: oneOf(FUNNEL_NEEDS_YOU_ITEM_TYPES) },
        emitter: LIVE,
    },
    {
        name: 'needs_you.item_viewed',
        measures: ['needs_you_response_time'],
        payload: { itemType: oneOf(FUNNEL_NEEDS_YOU_ITEM_TYPES) },
        emitter: LIVE,
    },
    {
        name: 'needs_you.item_decided',
        measures: ['needs_you_response_time'],
        payload: { itemType: oneOf(FUNNEL_NEEDS_YOU_ITEM_TYPES), elapsedMs: NUMBER },
        emitter: LIVE,
    },
    // Forms (#1774): the portal reports each, once per form session, split by flow. Opened is `form.started` and saved
    // is `form.completed`: there is no `form.opened` or `form.saved` beside them. A field error repeats per field per
    // save attempt; abandoned is leaving the form unsaved. A session ends once, as completed or abandoned, whichever
    // comes first: the other is dropped, so no session counts in both.
    {
        name: 'form.started',
        measures: ['form_completion'],
        payload: { flow: oneOf(FUNNEL_FORM_FLOWS) },
        emitter: LIVE,
    },
    {
        name: 'form.described',
        measures: ['form_completion'],
        payload: { flow: oneOf(FUNNEL_FORM_FLOWS) },
        emitter: LIVE,
    },
    {
        name: 'form.advanced_opened',
        measures: ['form_completion'],
        payload: { flow: oneOf(FUNNEL_FORM_FLOWS) },
        emitter: LIVE,
    },
    {
        name: 'form.field_error_shown',
        measures: ['form_completion'],
        payload: { flow: oneOf(FUNNEL_FORM_FLOWS), field: oneOf(FUNNEL_FORM_FIELDS) },
        emitter: LIVE,
    },
    {
        name: 'form.completed',
        measures: ['form_completion'],
        payload: { flow: oneOf(FUNNEL_FORM_FLOWS), elapsedMs: NUMBER },
        emitter: LIVE,
    },
    {
        name: 'form.abandoned',
        measures: ['form_completion'],
        payload: { flow: oneOf(FUNNEL_FORM_FLOWS), elapsedMs: NUMBER },
        emitter: LIVE,
    },

    // Recovery events (#1769): every event the Edge cases table of #1697's Onboarding spec names, in the table's
    // order, under `<area>.<past_tense_fact>` with the area from the table's Stage column. Each is owned by the
    // workstream that builds its recovery and wires its emit then; those whose trigger exists today are live.
    // A payload holds closed sets and numbers only: never the tool, account, teammate or text a recovery mentions.

    // Sign-up: workstream 1.
    recovery('signup_email_exists', 'signup.email_exists', ACCESS_AND_AI_ACCOUNT, ['first_session_success']),
    recovery('signup_oauth_failed', 'signup.oauth_failed', ACCESS_AND_AI_ACCOUNT, ['first_session_success'], {
        provider: oneOf(SIGNUP_PROVIDERS),
    }),
    recovery('signup_beta_blocked', 'signup.beta_blocked', ACCESS_AND_AI_ACCOUNT, ['first_session_success']),
    // Repeats: every resend is its own row.
    recovery('signup_code_resent', 'signup.code_resent', ACCESS_AND_AI_ACCOUNT, ['first_session_success']),

    // Setup: workstream 4. Resuming is live: `OnboardingService` records it with its own `setup_resumed` log entry.
    recovery('setup_resumed', 'setup.resumed', LIVE, ['first_session_success']),
    // Repeats: every time the chat goes off-script.
    recovery('chat_offscript', 'setup.chat_offscript', ONBOARDING_AND_NEEDS_YOU, ['profile_completion_and_naming']),
    // Live (#1742): a `work_profile` save of a known role with the person's own words records it, once per save.
    recovery('role_custom_mapped', 'setup.role_custom_mapped', LIVE, ['profile_completion_and_naming']),
    recovery('work_type_custom', 'setup.work_type_custom', ONBOARDING_AND_NEEDS_YOU, ['profile_completion_and_naming']),
    // Live (#1740): a team or role clone that kept a name "Pick names for me" filled records it, once per clone.
    recovery('names_auto_assigned', 'setup.names_auto_assigned', LIVE, ['profile_completion_and_naming']),

    // Cloning: workstream 2. A mismatched tool is never named, in any field. The tool mismatch is live: the review
    // read of a team or role template records it, once per review opened with a mismatch (C9, #1729).
    recovery('clone_no_match', 'clone.no_match', CATALOG_AND_CLONING, ['team_clone_rate']),
    recovery('clone_tool_mismatch', 'clone.tool_mismatch', LIVE, ['team_clone_rate']),
    recovery('clone_zero_tasks', 'clone.zero_tasks', CATALOG_AND_CLONING, ['team_clone_rate']),
    recovery('clone_update_available', 'clone.update_available', CATALOG_AND_CLONING, ['team_clone_rate']),

    // Upgrade: workstream 2. No card, amount or address: the plan and the prompt it started from.
    recovery('upgrade_failed', 'upgrade.failed', CATALOG_AND_CLONING, ['team_clone_rate'], {
        plan: oneOf(FUNNEL_PLANS),
        trigger: oneOf(FUNNEL_UPGRADE_TRIGGERS),
    }),
    recovery('payment_declined_region', 'upgrade.payment_declined_region', CATALOG_AND_CLONING, ['team_clone_rate'], {
        plan: oneOf(FUNNEL_PLANS),
    }),
    recovery('downgrade_teammates_paused', 'upgrade.teammates_paused', CATALOG_AND_CLONING, ['activation_gap'], {
        plan: oneOf(FUNNEL_PLANS),
    }),

    // AI provider: workstream 1. No key, model or account name: the step's drop-off reasons only. A refused key and
    // leaving the step are live (#1770): the connect call records every refusal, and an exit is stated at the step or
    // swept 24 hours after it was shown.
    recovery('provider_no_account', 'provider.no_account', ACCESS_AND_AI_ACCOUNT, ['ai_account_connection']),
    // Repeats: every refused key is its own row, so a silent exit can be explained by the last one.
    recovery('provider_key_invalid', 'provider.key_invalid', LIVE, ['ai_account_connection']),
    recovery('provider_billing_error', 'provider.billing_error', LIVE, ['ai_account_connection']),
    recovery('provider_model_fallback', 'provider.model_fallback', ACCESS_AND_AI_ACCOUNT, ['ai_account_connection']),
    recovery('provider_oauth_failed', 'provider.oauth_failed', ACCESS_AND_AI_ACCOUNT, ['ai_account_connection']),
    recovery('provider_lost', 'provider.lost', ACCESS_AND_AI_ACCOUNT, ['ai_account_connection']),
    recovery('key_in_chat_redacted', 'provider.key_in_chat_redacted', ACCESS_AND_AI_ACCOUNT, ['ai_account_connection']),
    recovery('spend_cap_set', 'provider.spend_cap_set', ACCESS_AND_AI_ACCOUNT, ['ai_account_connection']),
    // Once per user: leaving the step twice is one abandonment, and whichever exit is recorded first stands. The one
    // exit fact of the step: there is no `onboarding.ai_account_abandoned` beside it to count drop-off differently.
    // The AI account reminder sweep (#1718) also records it when a user who reached the step is due their reminder.
    recovery(
        'provider_step_abandoned',
        'provider.step_abandoned',
        LIVE,
        ['ai_account_connection'],
        { reason: oneOf(AI_ACCOUNT_ABANDON_REASONS) },
        { once: true },
    ),

    // Assembly: workstream 4. Both are live (#1743): the server-held assembly records a partial fail for every failed
    // teammate attempt its log shows, and a background completion when it finished with no client reading its log.
    // Repeats: every failure shown is its own row.
    recovery('assembly_partial_fail', 'assembly.partial_fail', LIVE, ['first_session_success']),
    // Once per user: a reload after the background assembly finished is the same completion.
    recovery(
        'assembly_background_complete',
        'assembly.background_complete',
        LIVE,
        ['first_session_success'],
        {},
        { once: true },
    ),

    // Tools: workstream 4. The tool and the account are never named.
    recovery('tool_admin_approval', 'tools.admin_approval_requested', ONBOARDING_AND_NEEDS_YOU, [
        'first_session_success',
    ]),
    recovery('tool_wrong_account', 'tools.wrong_account_shown', ONBOARDING_AND_NEEDS_YOU, ['first_session_success']),
    recovery('tools_none_connected', 'tools.none_connected', ONBOARDING_AND_NEEDS_YOU, ['first_session_success']),
    recovery('tool_scopes_viewed', 'tools.scopes_viewed', ONBOARDING_AND_NEEDS_YOU, ['first_session_success']),

    // First work: workstream 4. A failed or turned-down first run is live: the plan decision bridge emits it.
    // Setup needed is live (#1739): setup's read at `team_setup` records it, once per person, while the cloned team
    // misses a GitHub repo or a tool. No payload, so it never names the tool.
    recovery('team_setup_needed', 'first_work.team_setup_needed', LIVE, ['first_session_success'], {}, { once: true }),
    recovery('first_task_failed', 'first_work.task_failed', LIVE, ['first_session_success']),
    recovery(
        'first_task_rejected',
        'first_work.task_rejected',
        LIVE,
        ['first_session_success', 'time_to_first_approved_result'],
        { decision: oneOf(FUNNEL_TURNED_DOWN) },
    ),
    // Repeats: the 24 hour and the 48 hour reminder are two rows.
    recovery('gate_reminder_sent', 'first_work.gate_reminder_sent', ONBOARDING_AND_NEEDS_YOU, ['gate_abandonment'], {
        afterHours: NUMBER,
    }),
]

const BY_NAME: ReadonlyMap<string, FunnelEventEntry> = new Map(FUNNEL_EVENTS.map(entry => [entry.name, entry]))

/** The catalogue entry named `name`, or null when the funnel has no such event. */
export function funnelEvent(name: string): FunnelEventEntry | null {
    return BY_NAME.get(name) ?? null
}

/** Whether `emitter` is a live call site, or a declaration naming a real #1697 workstream (1 to 6). */
function isOwned(emitter: FunnelEmitter | null): boolean {
    if (!emitter) {
        return false
    }
    if (emitter.status === 'live') {
        return true
    }
    return (
        Number.isInteger(emitter.workstream) &&
        emitter.workstream >= WORKSTREAMS.min &&
        emitter.workstream <= WORKSTREAMS.max
    )
}

/** The names of entries with neither a live emitter nor a declared owning workstream (1 to 6). */
export function funnelCoverageGaps(entries: readonly FunnelEventEntry[]): string[] {
    return entries.filter(entry => !isOwned(entry.emitter)).map(entry => entry.name)
}

/** Where the catalogue and a list of spec rows disagree: spec names no entry implements, and entries claiming a row the list lacks. */
export type FunnelSpecGaps = { missing: string[]; unknown: string[] }

/**
 * Diffs `entries` against the spec's `specNames` in both directions (#1769): `missing` names each spec row no entry
 * implements, in the spec's order, and `unknown` names each entry claiming a spec row the list does not hold, in the
 * catalogue's order. An entry that claims no spec row (a happy-path fact) is neither.
 */
export function funnelSpecGaps(entries: readonly FunnelEventEntry[], specNames: readonly string[]): FunnelSpecGaps {
    const claimed = new Set(entries.flatMap(entry => (entry.spec === undefined ? [] : [entry.spec])))
    const table = new Set(specNames)
    return {
        missing: specNames.filter(spec => !claimed.has(spec)),
        unknown: entries.filter(entry => entry.spec !== undefined && !table.has(entry.spec)).map(entry => entry.name),
    }
}

/** The metrics the funnel is reported by (#1775): the headline, its sub-step, and the baseline figure with no target. */
export type FunnelMetricId = 'time_to_first_approved_result' | 'time_to_first_command' | 'initial_tool_connection'

/**
 * How one metric is computed from catalogue events. `start` and `end` are event names, both null when nothing in the
 * catalogue records the metric. `target` is null for a baseline. Exactly one definition is the headline.
 */
export type FunnelMetricDefinition = {
    id: FunnelMetricId
    name: string
    start: string | null
    end: string | null
    aggregate: 'median' | null
    target: string | null
    headline: boolean
    parent?: FunnelMetricId
}

/**
 * Every metric definition (#1775), per user and from event timestamps only (never an `elapsedMs` payload, whose start
 * point is not defined): the time from a user's first `start` row to their first `end` row at or after it, in one
 * enterprise. The cohort is the users whose first `start` falls in the window read; a user with no `end` yet is
 * pending, counted but left out of the median.
 *
 * - The headline (M7 of #1697) is time from sign-up to the first approved result. Its target is a baseline, then
 *   reduce: no number until one is measured.
 * - Time to first command is its sub-step: from the first approval prompt to the first decision of any kind, since the
 *   catalogue has no "first instruction" event and this issue adds none. It keeps the spec's target.
 * - Initial tool connection (M8) is a baseline with no target, no longer v2's over-75%. Nothing records a tool
 *   connected during team setup yet (#1739 owns that event), so it has no source and reads as not instrumented,
 *   never as 0.
 */
export const FUNNEL_METRICS: readonly FunnelMetricDefinition[] = [
    {
        id: 'time_to_first_approved_result',
        name: 'Time to first approved result',
        start: 'onboarding.signup_completed',
        end: 'onboarding.first_result_approved',
        aggregate: 'median',
        target: null,
        headline: true,
    },
    {
        id: 'time_to_first_command',
        name: 'Time to first command',
        start: 'onboarding.first_approval_prompted',
        end: 'onboarding.first_approval_decided',
        aggregate: 'median',
        target: 'Under 2 minutes',
        headline: false,
        parent: 'time_to_first_approved_result',
    },
    {
        id: 'initial_tool_connection',
        name: 'Initial tool connection',
        start: null,
        end: null,
        aggregate: null,
        target: null,
        headline: false,
    },
]

/**
 * The event names `definitions` name that `events` does not hold, each once, in the order met (a definition's
 * `start` before its `end`). A definition with no source names nothing, so it is never a gap.
 */
export function funnelMetricGaps(
    definitions: readonly FunnelMetricDefinition[],
    events: readonly FunnelEventEntry[] = FUNNEL_EVENTS,
): string[] {
    const known = new Set(events.map(entry => entry.name))
    const gaps: string[] = []
    for (const definition of definitions) {
        for (const name of [definition.start, definition.end]) {
            if (name !== null && !known.has(name) && !gaps.includes(name)) {
                gaps.push(name)
            }
        }
    }
    return gaps
}

/**
 * The ids of `definitions` that are also the name of an event in `events`, each once, in definition order: a metric
 * definition is reporting, never a funnel event.
 */
export function funnelMetricCollisions(
    definitions: readonly FunnelMetricDefinition[],
    events: readonly FunnelEventEntry[] = FUNNEL_EVENTS,
): string[] {
    const known = new Set(events.map(entry => entry.name))
    const collisions: string[] = []
    for (const definition of definitions) {
        if (known.has(definition.id) && !collisions.includes(definition.id)) {
            collisions.push(definition.id)
        }
    }
    return collisions
}

/**
 * The weekly AI-account drop-off report (M9, #1931): the definition of the Metabase question the CEOs receive every
 * Monday. Reading only; the rows are written by the funnel emitters (#1770). `sql` is the question's native query,
 * pasted into Metabase unchanged, so the reviewed text here is the text that runs.
 */
export interface AiAccountDropOffReport {
    name: string
    columns: readonly string[]
    events: readonly string[]
    sql: string
}

/** The column an exit's reason counts in: one of the shared reasons, or `unrecognised` for anything else. */
export type AiAccountReasonColumn = AiAccountAbandonReason | 'unrecognised'

/** The column an exit's setup path counts in: one of `SETUP_PATHS`, or `other_path` for a value outside them. */
export type AiAccountPathColumn = SetupPath | 'other_path'

const AI_ACCOUNT_DROP_OFF_REPORT_NAME = 'AI account drop-off (M9)'

/** The zone the report's weeks are read in. Always the named zone, never a fixed offset: London moves twice a year. */
const REPORT_ZONE = 'Europe/London'

/** A missing, `null` or out-of-set reason. */
const UNRECOGNISED_REASON = 'unrecognised' as const

/** A path that is set but is none of `SETUP_PATHS`, so a writer bug shows up rather than hiding in `unknown`. */
const OTHER_PATH = 'other_path' as const

/** Where an exit with no path at all (`null` or `''`) is counted: the same bucket as a path recorded as `unknown`. */
const NO_PATH: SetupPath = 'unknown'

/** The three facts of the AI account step the report counts, under the column each is counted in. */
const AI_ACCOUNT_REPORT_EVENTS = {
    shown: 'onboarding.ai_account_step_shown',
    connected: 'onboarding.ai_account_connected',
    exits: 'provider.step_abandoned',
} as const

/** The first week the report lists: the week of Thu 8 Oct 2026, when the first M9 row landed. */
const AI_ACCOUNT_REPORT_FIRST_WEEK = '2026-10-05'

const AI_ACCOUNT_REASON_COLUMNS: readonly AiAccountReasonColumn[] = [...AI_ACCOUNT_ABANDON_REASONS, UNRECOGNISED_REASON]

const AI_ACCOUNT_PATH_COLUMNS: readonly AiAccountPathColumn[] = [...SETUP_PATHS, OTHER_PATH]

const DAY_MS = 86_400_000

/** A YYYY-MM-DD calendar date, as written in the report and its SQL. */
const CALENDAR_DATE = /^(\d{4})-(\d{2})-(\d{2})$/

/** A value as a single-quoted SQL string literal. */
function sqlLiteral(value: string): string {
    return `'${value.replaceAll("'", "''")}'`
}

/**
 * The Monday of the Europe/London week a UTC `DATETIME` expression falls in, as a MySQL `DATE`. `WEEKDAY` is 0 on a
 * Monday whatever Metabase's start-of-week setting says, and `CONVERT_TZ` by named zone follows the clock changes.
 */
function londonMondaySql(utcDateTime: string): string {
    const local = `CONVERT_TZ(${utcDateTime}, '+00:00', ${sqlLiteral(REPORT_ZONE)})`
    return `DATE_SUB(DATE(${local}), INTERVAL WEEKDAY(${local}) DAY)`
}

/**
 * The question's native SQL (MySQL 8), built from the shared reason and path lists so a new value gets its own branch.
 *
 * - `weeks` is the zero-filled spine: every completed London week from the first one, the current week left out.
 * - `m9_rows` puts each of the three events in the London week of its `at` (UTC) and gives each exit exactly one
 *   reason column and one path column, so both splits add up to `exits` whatever the writer emitted.
 * - A set path is matched byte for byte (`CAST(setup_path AS BINARY)`), as `aiAccountPathColumn` does: under the
 *   column's case- and accent-insensitive collation `Chat` would count as `chat`, and the writer bug `other_path` is
 *   there to show would disappear.
 * - The spine and the rows are stacked with `UNION ALL` and counted per week, so an empty week reads 0.
 *
 * Aggregate counts only: no parameter, no identifier of anyone, and nothing to click through to.
 */
function aiAccountDropOffSql(): string {
    const thisMonday = londonMondaySql('UTC_TIMESTAMP()')
    const exit = sqlLiteral(AI_ACCOUNT_REPORT_EVENTS.exits)
    const reasonBranches = AI_ACCOUNT_ABANDON_REASONS.map(
        reason => `                    WHEN ${sqlLiteral(reason)} THEN ${sqlLiteral(reason)}`,
    )
    const pathBranches = SETUP_PATHS.map(
        path => `                    WHEN setup_path = ${sqlLiteral(path)} THEN ${sqlLiteral(path)}`,
    )
    const eventCounts = Object.entries(AI_ACCOUNT_REPORT_EVENTS).map(
        ([column, event]) => `    COUNT(CASE WHEN event = ${sqlLiteral(event)} THEN 1 END) AS \`${column}\``,
    )
    const reasonCounts = AI_ACCOUNT_REASON_COLUMNS.map(
        column => `    COUNT(CASE WHEN reason_column = ${sqlLiteral(column)} THEN 1 END) AS \`${column}\``,
    )
    const pathCounts = AI_ACCOUNT_PATH_COLUMNS.map(
        column => `    COUNT(CASE WHEN path_column = ${sqlLiteral(column)} THEN 1 END) AS \`${column}\``,
    )
    return [
        `-- ${AI_ACCOUNT_DROP_OFF_REPORT_NAME}: one row per completed Monday-Sunday Europe/London week, newest first.`,
        '-- Generated from packages/shared/src/funnel.ts (#1931). Paste it unchanged; edit it there.',
        'WITH RECURSIVE weeks (week_start) AS (',
        `    SELECT DATE ${sqlLiteral(AI_ACCOUNT_REPORT_FIRST_WEEK)}`,
        '    UNION ALL',
        '    SELECT week_start + INTERVAL 7 DAY',
        '    FROM weeks',
        `    WHERE week_start + INTERVAL 7 DAY < ${thisMonday}`,
        '),',
        'm9_rows AS (',
        '    SELECT',
        `        ${londonMondaySql('at')} AS week_start,`,
        '        event,',
        `        CASE WHEN event = ${exit} THEN`,
        "                CASE JSON_UNQUOTE(JSON_EXTRACT(payload, '$.reason'))",
        ...reasonBranches,
        `                    ELSE ${sqlLiteral(UNRECOGNISED_REASON)}`,
        '                END',
        '        END AS reason_column,',
        `        CASE WHEN event = ${exit} THEN`,
        '                CASE',
        `                    WHEN setup_path IS NULL OR setup_path = '' THEN ${sqlLiteral(NO_PATH)}`,
        `                    WHEN CAST(setup_path AS BINARY) NOT IN (${SETUP_PATHS.map(sqlLiteral).join(', ')}) THEN ${sqlLiteral(OTHER_PATH)}`,
        ...pathBranches,
        `                    ELSE ${sqlLiteral(OTHER_PATH)}`,
        '                END',
        '        END AS path_column',
        '    FROM funnel_events',
        `    WHERE event IN (${Object.values(AI_ACCOUNT_REPORT_EVENTS).map(sqlLiteral).join(', ')})`,
        '),',
        'counted AS (',
        '    SELECT week_start, NULL AS event, NULL AS reason_column, NULL AS path_column FROM weeks',
        '    UNION ALL',
        '    SELECT week_start, event, reason_column, path_column FROM m9_rows',
        '    WHERE week_start IN (SELECT week_start FROM weeks)',
        ')',
        'SELECT',
        ['    week_start AS `week_start`', ...eventCounts, ...reasonCounts, ...pathCounts].join(',\n'),
        'FROM counted',
        'GROUP BY week_start',
        'ORDER BY `week_start` DESC',
    ].join('\n')
}

/**
 * The M9 report: last week's drop-off at the AI account step for all enterprises, as shown, connected and exits, the
 * exits split by reason and by setup path. Every column after `week_start` counts matching rows with
 * `COUNT(CASE WHEN ... THEN 1 END)`, so an empty week reads 0. Each of the three events is recorded once per user, so
 * rows are founders.
 */
export const AI_ACCOUNT_DROP_OFF_REPORT: AiAccountDropOffReport = {
    name: AI_ACCOUNT_DROP_OFF_REPORT_NAME,
    columns: [
        'week_start',
        ...Object.keys(AI_ACCOUNT_REPORT_EVENTS),
        ...AI_ACCOUNT_REASON_COLUMNS,
        ...AI_ACCOUNT_PATH_COLUMNS,
    ],
    events: Object.values(AI_ACCOUNT_REPORT_EVENTS),
    sql: aiAccountDropOffSql(),
}

const LONDON_DATE = new Intl.DateTimeFormat('en-GB', {
    timeZone: REPORT_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
})

/** London's wall clock at `at`, read back as if it were UTC: the instant plus London's offset then. */
function londonWallClockMs(at: Date): number {
    const parts = LONDON_DATE.formatToParts(at)
    const part = (type: Intl.DateTimeFormatPartTypes): number => Number(parts.find(row => row.type === type)?.value)
    return Date.UTC(part('year'), part('month') - 1, part('day'), part('hour'), part('minute'), part('second'))
}

/** A UTC midnight's calendar date as YYYY-MM-DD. */
function calendarDate(utcMidnightMs: number): string {
    return new Date(utcMidnightMs).toISOString().slice(0, 10)
}

/** The Monday (YYYY-MM-DD) of the Europe/London week a UTC instant falls in. Weeks run Monday to Sunday. */
export function londonWeekStart(at: Date): string {
    const wallClock = londonWallClockMs(at)
    const localMidnight = wallClock - (((wallClock % DAY_MS) + DAY_MS) % DAY_MS)
    const daysSinceMonday = (new Date(localMidnight).getUTCDay() + 6) % 7
    return calendarDate(localMidnight - daysSinceMonday * DAY_MS)
}

/** London's offset from UTC, in ms, at an instant: +1 h in summer time, 0 in winter. */
function londonOffsetMs(instantMs: number): number {
    return londonWallClockMs(new Date(instantMs)) - instantMs
}

/**
 * The UTC instant London's clock reads midnight at the start of a calendar day (given as that day's UTC midnight).
 * The offset is taken once near the answer and once at it, so a day the clocks change on lands right; London changes
 * at 01:00 UTC, so it never skips or repeats midnight.
 */
function londonMidnight(utcMidnightMs: number): Date {
    const nearby = utcMidnightMs - londonOffsetMs(utcMidnightMs)
    return new Date(utcMidnightMs - londonOffsetMs(nearby))
}

/**
 * The UTC window [start, end) of the Europe/London week starting on the given Monday (YYYY-MM-DD): 168 hours, or 167
 * and 169 in the weeks the clocks go forward and back. These are the reference ranges a direct count is taken over.
 */
export function londonWeekWindow(weekStart: string): { start: Date; end: Date } {
    const match = CALENDAR_DATE.exec(weekStart)
    const monday = match ? Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])) : Number.NaN
    if (Number.isNaN(monday) || calendarDate(monday) !== weekStart) {
        throw new RangeError(`londonWeekWindow: ${JSON.stringify(weekStart)} is not a YYYY-MM-DD date`)
    }
    if (new Date(monday).getUTCDay() !== 1) {
        throw new RangeError(`londonWeekWindow: ${weekStart} is not a Monday`)
    }
    return { start: londonMidnight(monday), end: londonMidnight(monday + 7 * DAY_MS) }
}

/** The report column an exit reason counts in: its own, or `unrecognised` when missing, empty or out of the set. */
export function aiAccountReasonColumn(reason: unknown): AiAccountReasonColumn {
    return AI_ACCOUNT_ABANDON_REASONS.find(known => known === reason) ?? UNRECOGNISED_REASON
}

/** The report column a setup path counts in: its own, `unknown` for none, or `other_path` for any other value. */
export function aiAccountPathColumn(path: unknown): AiAccountPathColumn {
    if (path === null || path === undefined || path === '') {
        return NO_PATH
    }
    return SETUP_PATHS.find(known => known === path) ?? OTHER_PATH
}
